import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { RoutePaths } from 'src/app/shared/services/basic.service';
import { environment } from 'src/environments/environment';
import { AlcmeonContextService } from '../alcmeon-context/alcmeon-context.service';
import { TrackService } from '../track/track.service';
import { ReconnectingWebSocket } from './reconnecting-websocket';
import { WebSocketNotification } from './model';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  // A new websocket will be opened on each login, and closed on logout/error;
  // this observable "merges" the websocket events from these successive websockets
  message$ = new Subject<WebSocketNotification>();

  private websocket: ReconnectingWebSocket;

  constructor(
    private http: HttpClient,
    router: Router,
    private trackService: TrackService,
    private alcmeonContext: AlcmeonContextService
  ) {
    if (!environment.bundledWithAngularJSApp) {
      router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(event => this.handleNavigationEndEvent(event));
    }
    // when bundledWithAngularJSApp, the websocket is opened / closed from router.config.js
  }

  createAndOpenWebSocketFromAJSApp(companyId: string, onMessageFn: (message) => void): void {
    this.websocket = new ReconnectingWebSocket(this.http, companyId, this.trackService);
    this.websocket.message$.subscribe(message => {
      onMessageFn(message); // forward message to ajs app
      this.message$.next(message); // forward message to angular app
    });
    this.websocket.open();
  }

  // common to native and desktop
  closeWebSocket() {
    this.websocket.close(); // this will complete this.websocket.message$ and hence unsubscribe our subscriptions
    this.websocket = null;
  }

  // Whenever the router navigates to an auth'ed route, if there is no websocket connection yet, create one.
  // Conversely, whenever the router navigates to a non-auth'ed route, if there is an open websocket connection, close it.
  // This is only called for the native app (in the desktop app, the angularjs router handles all routing)
  private handleNavigationEndEvent(event: NavigationEnd): void {
    const url = event.urlAfterRedirects;
    if (url.startsWith(RoutePaths.AuthRoutesPrefix) && !this.websocket) {
      this.websocket = new ReconnectingWebSocket(this.http, this.alcmeonContext.companyId, this.trackService);
      this.websocket.message$.subscribe(message => this.message$.next(message));
      this.websocket.open();
    } else if (!url.startsWith(RoutePaths.AuthRoutesPrefix) && this.websocket) {
      this.closeWebSocket();
    }
  }
}
